import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthStore } from '../../stores/auth.store';
import { UiStore } from '../../stores/ui.store';
import { AuthManagerService } from '../../managers/auth-manager.service';
import { LogUtilityService } from '../logging/log-utility.service';
import { environment } from '../../../environments/environment';

const authBypassPaths = ['auth/login', 'auth/send-login-verification'];
const errorBypassPaths = ['auth/refresh'];

export function shouldBypassAuth(url: string): boolean {
  return authBypassPaths.some((path) => url.includes(path));
}

const ALREADY_RETRIED = new HttpContextToken<boolean>(() => false);

let refreshInFlight: Promise<boolean> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  if (shouldBypassAuth(req.url)) {
    return next(req);
  }

  const authStore = inject(AuthStore);
  const uiStore = inject(UiStore);
  const router = inject(Router);
  const authManager = inject(AuthManagerService);
  const logUtility = inject(LogUtilityService);
  const token = authStore.token();

  const outgoing = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  const shouldBypassErrors = errorBypassPaths.some((path) => req.url.includes(path));

  return next(outgoing).pipe(
    catchError((error: HttpErrorResponse) => {
      if (shouldBypassErrors || error.status !== 401) {
        return throwError(() => error);
      }

      if (req.context.get(ALREADY_RETRIED)) {
        redirectToLogin(authStore, uiStore, router, logUtility);
        return throwError(() => error);
      }

      refreshInFlight ??= authManager.refreshAuth().finally(() => {
        refreshInFlight = null;
      });

      return from(refreshInFlight).pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            redirectToLogin(authStore, uiStore, router, logUtility);
            return throwError(() => error);
          }

          const newToken = authStore.token();
          const retryContext = req.context;
          retryContext.set(ALREADY_RETRIED, true);

          const retry = newToken
            ? req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
                context: retryContext,
              })
            : req.clone({ context: retryContext });

          return next(retry);
        })
      );
    })
  );
};

function redirectToLogin(
  authStore: AuthStore,
  uiStore: UiStore,
  router: Router,
  logUtility: LogUtilityService,
): void {
  authStore.clearAuth();
  logUtility.clearAuthenticatedUser();
  uiStore.showToast('Session expired. Please log in again.', 'error');
  void router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
}
